// ============================================================
// Écran Apprentissage — la promesse, rendue vérifiable.
// ------------------------------------------------------------
// « Le programme devient plus juste à chaque devis » est une phrase de
// vendeur tant que personne ne peut la contredire. Cet écran est ce qui
// la rend réfutable : si les courbes ne descendent pas, le programme
// n'apprend pas, et l'écran le dit avec les mêmes chiffres.
//
// C'est la première des deux exigences non négociables du §2bis. Chez
// AGC, personne ne pouvait répondre à la question — donc « l'IA
// apprend » y est resté invérifiable.
//
// ── Deux partis pris d'affichage ──
//
// **On affiche le nombre de corrections À CÔTÉ de chaque écart.** Une
// médiane sur trois points n'est pas une tendance, et une courbe qui
// descend sur des effectifs qui s'effondrent ne veut rien dire. Le
// dénominateur reste visible, toujours.
//
// **Ce qui est écarté est compté et dit.** Les familles sous le seuil
// n'ont pas de médiane affichable ; leur nombre apparaît quand même. Une
// troncature muette se lit comme « on a tout couvert ».

import { useCallback, useMemo } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Table from '../../ui/Table.jsx';
import Badge from '../../ui/Badge.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';
import { formatNumber } from '../../lib/format.js';
import Sparkline from './Sparkline.jsx';
import SurveillanceCarte from './SurveillanceCarte.jsx';
import ChampsCorriges from './ChampsCorriges.jsx';
import IndicateursCarte from './IndicateursCarte.jsx';
import EcartCarte from './EcartCarte.jsx';
// ── UN SEUL NOM PAR POSTE, ET IL VIT DANS `shared/` ──
// Cet écran portait son propre vocabulaire — « Mise en train (1re
// pièce) » là où la gamme affichait « prototype ». Le même poste sous
// deux mots, c'est-à-dire deux postes pour qui lit les deux écrans.
import { BLOCS_GAMME } from '@shared/gamme.js';
import './apprentissage.css';

/** Ce que le bloc RECOUVRE, sous son nom — par BLOC depuis le 28 septembre
 *  2026 : le contrôle rejoint ses voisins. Le mot dit quoi, l'aide dit ce
 *  qu'il contient. C'est l'explication qui manquait, pas le mot. */
const BLOC = Object.fromEntries(BLOCS_GAMME.map((b) => [b.cle, b]));


export function ApprentissagePage() {
  const charger = useCallback(() => api.get('/apprentissage'), []);
  const { data, loading, error, reload } = useResource(charger);

  /**
   * L'écran est-il RÉELLEMENT vide ?
   *
   * Il ne testait que les corrections — `ecart` et `volume`. Or les deux
   * indicateurs du §2bis se remplissent dès qu'une pièce porte une
   * gamme, bien avant qu'une correction existe : au premier devis
   * chiffré, la page affichait « aucune correction capturée » alors
   * qu'elle avait déjà les deux chiffres qui comptent le plus.
   *
   * Un état vide qui cache une donnée n'est pas un état vide, c'est un
   * écran qui ment sur ce qu'il sait.
   */
  const rien = useMemo(
    () => data && !data.ecart.length && !data.volume.length && !(data.indicateurs ?? []).length
      // Et le RÉEL. Sans cette ligne, l'écran affichait « aucune
      // correction capturée » alors qu'il tenait trente-sept retours
      // d'atelier — un état vide qui cache une donnée n'est pas un état
      // vide, c'est un écran qui ment sur ce qu'il sait.
      && !(data.atelier ?? []).length,
    [data],
  );

  if (loading) return <SkeletonRows rows={5} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  if (rien) {
    return (
      <Card>
        <CardHead title="Apprentissage" />
        <CardBody>
          <EmptyState title="Aucune correction capturée pour l’instant">
            Ces courbes se remplissent toutes seules : dès qu’une valeur proposée par un agent est
            modifiée avant enregistrement, l’écart est stocké. La première mesure apparaîtra au
            premier devis corrigé. Un écran vide ici est une information juste, pas une panne.
          </EmptyState>
        </CardBody>
      </Card>
    );
  }


  const replis = data.replis ?? [];
  // Le réel : la couche 4 du §2bis, et la seule mesure qui ne dépende
  // d'aucune estimation.
  const atelier = data.atelier ?? [];
  const atelierMois = data.atelier_mensuel ?? [];
  const vol = data.atelier_volume ?? {};
  const morts = replis.filter((r) => r.mort);


  return (
    <div className="stack">
      {/* ── LE TAUX DE REPLI, EN TÊTE — invariant 24 ──
          Il est au-dessus des courbes d'apprentissage, et pas en bas de
          page, parce qu'il les EXPLIQUE : un chemin IA mort ne fait pas
          baisser l'écart, il le fige. Le 7 août, Haiku refusait le
          paramètre `effort` et le rappel de densité échouait à 100 % —
          invisible, parce que le repli sur la famille est juste à 1-3 %
          près. Un repli assez bon masque la panne de ce qu'il remplace. */}
      {replis.length ? (
        <Card>
          <CardHead title="Les chemins qui retombent">
            {morts.length ? (
              <Badge tone="danger">{morts.length} chemin(s) mort(s)</Badge>
            ) : (
              <Badge tone="ok">aucun chemin mort</Badge>
            )}
          </CardHead>
          <CardBody className="stack">
            <p className="field__hint">
              Un repli est une solution de secours qui a servi. Elle est souvent correcte — c’est
              justement le danger : <strong>un repli assez bon masque la panne de ce qu’il
              remplace</strong>. Un chemin qui retombe à 100 % n’est pas dégradé, il est mort.
            </p>
            <Table
              columns={[
                { key: 'chemin', label: 'Chemin' },
                { key: 'tentatives', label: 'Tentatives', numeric: true },
                { key: 'replis', label: 'Replis', numeric: true },
                {
                  key: 'taux_pct',
                  label: 'Taux',
                  numeric: true,
                  render: (r) => (
                    <span className="row">
                      {r.taux_pct} %
                      {r.mort ? <Badge tone="danger">mort</Badge> : null}
                    </span>
                  ),
                },
                {
                  key: 'derniere_cause',
                  label: 'Dernière cause',
                  // La cause, pas seulement le compte : « appel_en_echec »
                  // et « aucune_valeur_rendue » demandent deux actions
                  // opposées.
                  render: (r) => r.derniere_cause ?? '—',
                },
              ]}
              rows={replis}
              rowKey={(r) => r.chemin}
            />
          </CardBody>
        </Card>
      ) : null}

      {/* ══════════════════════════════════════════════════════════
          LE RÉEL — ce que l'atelier dit du devis. 26 août 2026.
          ══════════════════════════════════════════════════════════
          Cet écran lisait UNIQUEMENT les corrections du deviseur, et
          cette table est vide : le mécanisme de capture attend une
          déclaration qu'aucun composant n'émet depuis le 11 août.
          L'écran qui doit prouver que le programme apprend n'affichait
          donc rien.

          Pendant ce temps, trente-sept retours d'atelier dormaient dans
          la base. C'est la COUCHE 4 du §2bis — la seule vérité terrain,
          celle qui ne dépend pas des estimations du deviseur.

          > Le signal existait. L'écran qui doit le montrer regardait
          > ailleurs.

          Il vient EN TÊTE parce que c'est la mesure la plus solide du
          dispositif : elle ne vient d'aucun modèle et d'aucune
          déclaration d'écran — elle vient des machines. */}
      {atelier.length ? (
        <Card>
          <CardHead title="Ce que l’atelier dit du devis">
            <Badge tone={vol.comparables >= 30 ? 'ok' : 'warn'}>
              {vol.comparables} opération(s) comparée(s)
            </Badge>
          </CardHead>
          <CardBody className="stack">
            <p className="field__hint">
              Temps <strong>devisé</strong> contre temps <strong>réellement passé</strong>, relevé
              sur la fiche de production et validé au bureau. C’est la seule mesure qui ne dépend
              d’aucune estimation : ni de l’agent, ni du deviseur.
              {vol.comparables < 30 ? (
                <> <strong>Sur {vol.comparables} opérations, c’est un premier signal,
                  pas encore une statistique</strong> — à lire comme une direction.</>
              ) : null}
            </p>

            <Table
              columns={[
                {
                  key: 'bloc',
                  label: 'Bloc',
                  // Le nom, et sous lui ce qu'il recouvre. Un poste
                  // nommé sans être expliqué se relit de travers trois
                  // mois plus tard.
                  render: (r) => (
                    <>
                      <strong>{BLOC[r.bloc]?.label ?? r.bloc}</strong>
                      {BLOC[r.bloc]?.aide
                        ? <span className="apprentissage__aide"> — {BLOC[r.bloc].aide}</span>
                        : null}
                    </>
                  ),
                },
                { key: 'devise_min', label: 'Devisé', numeric: true, render: (r) => `${formatNumber(r.devise_min)} min` },
                { key: 'reel_min', label: 'Réel', numeric: true, render: (r) => `${formatNumber(r.reel_min)} min` },
                {
                  key: 'reel_pct_du_devise',
                  label: 'Réel / devisé',
                  numeric: true,
                  // Au-dessus de 100 on sous-estime, en dessous on
                  // surestime. Les deux coûtent, dans des sens opposés :
                  // l'un fait perdre de l'argent, l'autre fait perdre
                  // l'affaire.
                  render: (r) => (
                    <span className="row">
                      {r.reel_pct_du_devise != null ? `${r.reel_pct_du_devise} %` : '—'}
                      {r.reel_pct_du_devise > 130 ? <Badge tone="danger">sous-estimé</Badge> : null}
                      {r.reel_pct_du_devise < 70 ? <Badge tone="warn">surestimé</Badge> : null}
                    </span>
                  ),
                },
                { key: 'lignes', label: 'Lignes', numeric: true },
              ]}
              rows={atelier}
              rowKey={(r) => r.bloc}
              caption="Écart devisé / réel par bloc"
            />

            <p className="field__hint">
              Un poste au-dessus de 100 % est <strong>sous-estimé</strong> : le devis perd de
              l’argent. En dessous, il est <strong>surestimé</strong> : le devis perd l’affaire.
              Les deux se corrigent, et pas dans le même sens.
            </p>

            {atelierMois.length > 1 ? (
              <>
                <p className="caps">Mois par mois — cette courbe doit tendre vers 100 %</p>
                <Sparkline
                  points={atelierMois.map((p) => ({ x: p.mois, y: p.reel_pct_du_devise }))}
                  label="Réel en % du devisé"
                />
              </>
            ) : (
              <p className="field__hint">
                La courbe mois par mois apparaîtra au deuxième mois de retours. C’est elle qui
                dira si le programme s’améliore — le tableau ci-dessus ne dit que l’état
                d’aujourd’hui.
              </p>
            )}

            {/* Le DÉNOMINATEUR, toujours visible. « Le prototype est
                sous-estimé de 114 % » ne veut rien dire tant qu'on ignore
                si c'est sur quatorze lignes ou sur quatorze cents. */}
            <div className="grid-4">
              <div>
                <p className="caps">Retours reçus</p>
                <p className="num prechiffrage__valeur">{vol.recus}</p>
              </div>
              <div>
                <p className="caps">Validés au bureau</p>
                <p className="num prechiffrage__valeur">{vol.valides}</p>
              </div>
              <div>
                <p className="caps">Comparables</p>
                <p className="num prechiffrage__valeur">{vol.comparables}</p>
              </div>
              <div>
                {/* Chez AGC : 58 lignes captées, 0 lien confirmé, et
                    personne ne le savait. Ce chiffre est là pour que ça
                    ne se reproduise pas. */}
                <p className="caps">Photos rattachées</p>
                <p className="num prechiffrage__valeur">
                  {vol.par_photo ? `${vol.taux_rattachement_photo} %` : '—'}
                </p>
              </div>
            </div>

            {vol.recus > vol.comparables ? (
              <p className="field__hint">
                {vol.recus - vol.comparables} retour(s) ne sont pas comparables : brouillon non
                validé, temps non renseigné, ou retour donné en trois totaux plutôt qu’opération
                par opération. Ce n’est pas une perte — c’est ce que dit la fiche telle qu’elle
                revient de l’atelier.
              </p>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {/* ══════════════════════════════════════════════════════════
          LES DEUX INDICATEURS DU §2bis — EN TÊTE, avant tout le reste.
          ══════════════════════════════════════════════════════════
          Tout ce qui suit mesure le SIGNAL : combien de corrections, sur
          quels champs, avec quel écart. C'est utile et ce n'est pas la
          question. La question est : est-ce que ça SERT ?

          Chez AGC, personne ne pouvait y répondre — donc « l'IA apprend »
          y est resté une affirmation invérifiable. C'est exactement le
          genre de promesse que Processia refuse de vendre. */}
      <IndicateursCarte indicateurs={data.indicateurs ?? []} />

      <EcartCarte ecart={data.ecart} acceptation={data.acceptation} />

      <Card>
        <CardHead title="Par famille de pièce">
          {data.familles.trop_peu > 0 ? (
            <Badge tone="neutral">
              {data.familles.trop_peu} famille(s) sous {data.familles.seuil} corrections
            </Badge>
          ) : null}
        </CardHead>
        <CardBody className="stack">
          <p className="field__hint">
            Une courbe unique moyenne des pièces qui n’ont rien à voir. Le programme peut être
            devenu très juste sur les rondelles tournées et rester faux sur les prismatiques :
            agrégé, ça donne une ligne plate qui n’appelle aucune décision. Segmenté, ça dit{' '}
            <strong>où</strong> porter l’effort.
          </p>
          {data.familles.familles.length === 0 ? (
            <p className="field__hint">
              Aucune famille n’atteint encore {data.familles.seuil} corrections. Une médiane sur
              deux points n’est pas une médiane — elle n’est donc pas affichée.
            </p>
          ) : (
            data.familles.familles.map((f) => (
              <div key={f.famille} className="apprentissage__famille">
                <div className="row row--between">
                  <strong>{f.famille}</strong>
                  <span className="field__hint">{f.total} pièce(s) corrigée(s)</span>
                </div>
                {['mise_en_route', 'production'].map((bloc) => (
                  <div key={bloc}>
                    <p className="caps">{bloc === 'mise_en_route' ? 'Mise en route' : 'Production'}</p>
                    <Sparkline
                      points={f.points.map((p) => ({ x: p.mois, y: p[bloc].mediane_absolue }))}
                      sens="descend"
                    />
                  </div>
                ))}
              </div>
            ))
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Le signal capté" />
        <CardBody className="stack">
          <p className="field__hint">
            La <strong>perception</strong> (une lecture de plan corrigée) se mesure et ne se
            réinjecte jamais. La <strong>dérivation</strong> (une opération ajoutée ou retirée) se
            réinjecte. Les additionner masquerait un dispositif qui capte beaucoup et n’apprend
            rien. Une correction <strong>justifiée</strong> vaut plus que dix chiffrées : un chiffre
            ne vaut que pour sa pièce, une phrase vaut règle.
          </p>
          <Table
            caption="Volume et densité du signal"
            columns={[
              { key: 'mois', label: 'Mois' },
              { key: 'total', label: 'Total' },
              { key: 'perception', label: 'Perception (lecture)' },
              { key: 'derivation', label: 'Dérivation (gamme)' },
              {
                key: 'justifiees',
                label: 'Justifiées',
                render: (r) =>
                  `${r.justifiees}${r.total ? ` — ${Math.round((r.justifiees / r.total) * 100)} %` : ''}`,
              },
            ]}
            rows={data.volume}
            getRowKey={(r) => r.mois}
          />
        </CardBody>
      </Card>

      <SurveillanceCarte />
      <ChampsCorriges champs={data.champs} />
    </div>
  );
}

export default ApprentissagePage;
